import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePayrollRuns, useCreatePayrollRun, useGeneratePayroll, useFinalizePayroll, useDeletePayrollRun, useBranches } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Plus, Play, CheckCircle, Trash2, Eye, FileSpreadsheet, Calendar } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';
import { useAuth } from '@/context/AuthContext';

type PayrollRunStatus = 'draft' | 'processing' | 'completed' | 'finalized' | 'cancelled' | 'generating' | 'generated' | 'paid';

const statusConfig: Record<PayrollRunStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'bg-gray-100 text-gray-700' },
  processing: { label: 'Расчёт...', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Готово', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Отменено', color: 'bg-red-100 text-red-700' },
  generating: { label: 'Расчёт...', color: 'bg-yellow-100 text-yellow-700' },
  generated: { label: 'Готово', color: 'bg-blue-100 text-blue-700' },
  finalized: { label: 'Завершено', color: 'bg-green-100 text-green-700' },
  paid: { label: 'Оплачено', color: 'bg-emerald-100 text-emerald-700' },
};

export default function PayrollRunsPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canRunPayroll = can('run_payroll');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const { data: runsData, isLoading, refetch } = usePayrollRuns({ page, per_page: perPage, month });
  const { data: branchesData } = useBranches({ per_page: 100 });
  const createMutation = useCreatePayrollRun();
  const generateMutation = useGeneratePayroll();
  const finalizeMutation = useFinalizePayroll();
  const deleteMutation = useDeletePayrollRun();

  const { items: runs, meta } = extractPaginatedData<any>(runsData, page, perPage);
  const { items: branches } = extractPaginatedData<any>(branchesData, 1, 100);
  const hasRunningRuns = runs.some((run: any) => ['processing', 'generating'].includes(run.status));

  useEffect(() => {
    if (!hasRunningRuns) {
      return;
    }

    const interval = setInterval(() => {
      refetch();
    }, 3000);

    return () => clearInterval(interval);
  }, [hasRunningRuns, refetch]);

  const handleGenerate = async (id: number) => {
    if (window.confirm('Создать расчёт штрафов для всех сотрудников?')) {
      await generateMutation.mutateAsync(id);
      refetch();
    }
  };

  const handleFinalize = async (id: number) => {
    if (window.confirm('Завершить этот расчёт?')) {
      await finalizeMutation.mutateAsync(id);
      refetch();
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Удалить этот расчёт штрафов?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Расчёты штрафов</h1>
        {canRunPayroll && (
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Новый расчёт
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Создать месячный расчёт штрафов"
      >
        <PayrollRunForm
          branches={branches}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Month Filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <Input
            type="month"
            value={month}
            onChange={(e) => {
              setPage(1);
              setMonth(e.target.value);
            }}
            className="w-48"
          />
        </div>
      </div>

      {/* Payroll Runs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Название</th>
                  <th className="text-left py-3 px-4 font-semibold">Филиал</th>
                  <th className="text-left py-3 px-4 font-semibold">Период</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-left py-3 px-4 font-semibold">Сотрудники</th>
                  <th className="text-left py-3 px-4 font-semibold">Итого штрафов</th>
                  <th className="text-left py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      За этот месяц расчётов нет
                    </td>
                  </tr>
                ) : (
                  runs.map((run: any) => {
                    const status = (run.status as PayrollRunStatus) || 'draft';
                    const config = statusConfig[status] || statusConfig.draft;
                    return (
                      <tr key={run.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{run.name}</td>
                        <td className="py-3 px-4">{run.branch?.name || '-'}</td>
                        <td className="py-3 px-4">
                          {run.period_start} — {run.period_end}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                            {config.label}
                          </span>
                        </td>
                        <td className="py-3 px-4">{run.total_employees || run.payslips_count || 0}</td>
                        <td className="py-3 px-4 font-semibold text-red-600">
                          {run.total_deductions
                            ? Number(run.total_deductions).toLocaleString('ru-RU') + ' сум'
                            : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Link to={`/payroll/runs/${run.id}`}>
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            {canRunPayroll && status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleGenerate(run.id)}
                                disabled={generateMutation.isPending}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            {canRunPayroll && (status === 'completed' || status === 'generated') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFinalize(run.id)}
                                disabled={finalizeMutation.isPending}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {canRunPayroll && ['draft', 'processing', 'generating', 'generated', 'completed'].includes(status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(run.id)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

interface PayrollRunFormProps {
  branches: any[];
  onSubmit: (data: { branch_id: number; period_start: string; period_end: string; name?: string }) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function PayrollRunForm({ branches, onSubmit, onCancel, isLoading }: PayrollRunFormProps) {
  const [branchId, setBranchId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      branch_id: Number(branchId),
      period_start: periodStart,
      period_end: periodEnd,
      name: name || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Филиал *</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="">Выберите филиал</option>
            {branches.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Название</label>
          <Input
            placeholder="Например: расчёт штрафов за апрель 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Начало периода *</label>
          <Input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Конец периода *</label>
          <Input
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Создание...' : 'Создать'}
        </Button>
      </div>
    </form>
  );
}

